import { Button } from '../../Button';
import { formRules } from '../FormRules';
import { Input } from '../../Inputs/Input';
import { ChangeEvent, useEffect, useState } from 'react';
import { apiService } from '../../../services/api';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppSelector, useToast } from '../../../hooks';
import {
  removeNonNumericChars,
  formatCpf,
  formatPhone,
  formatCEP,
  formatStringMoney,
  readMoney,
  formatMoney,
  formatAddress,
} from '../../../utils/formatters';
import { Switch } from '../../Switch';
import { capitalize } from '@mui/material';
import { Notification } from '../NotificationComponent';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Datepicker } from '../../Datepicker';
import { UsersFormProps } from '../../../pages/Register/form-props.types';
import { UpdateUserReq, UserRole } from '../../../services/api.types';
import { useNavigate } from 'react-router-dom';
import { usersService } from '../../../services/users.service';
import { viaCepService } from '../../../services/viacep';

interface ResponsibleFormValues {
  fullName: string;
  displayName: string;
  email: string;
  active?: boolean;
  role?: UserRole;
  cpf: string;
  phone: string;
  postalCode: string;
  state?: string;
  city?: string;
  province?: string;
  address?: string;
  addressNumber: string;
  addressComplement?: string;
  hourElementaryChargeValue: string;
  hourUniversityChargeValue: string;
  notification: {
    notifyEmail: boolean;
    //notifySMS: boolean;
    notifyWhatsapp: boolean;
  };
  isStudent: boolean;
  birthdate?: dayjs.Dayjs;
  homeClassesPlace?: string;
}

export function ResponsibleRegisterForm({
  mode,
  userToUpdate,
}: Readonly<UsersFormProps>) {
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formattedAddress, setFormattedAddress] = useState<string>('');
  const user = useAppSelector((state) => state.user);
  const selfEdit = userToUpdate?.id === user?.id;
  const isAdminOrEmployee =
    user?.role === UserRole.ADMIN || user?.role === UserRole.EMPLOYEE;
  const navigate = useNavigate();

  const fetchAddressData = async (postalCode: string) => {
    postalCode = removeNonNumericChars(postalCode);
    if (postalCode.length !== 8) return;
    return await viaCepService.getAddress(postalCode).then((res) => {
      if (res.erro) {
        toast.error('CEP não encontrado');
        eraseAddressData();
      }
      return res;
    });
  };

  const handleViaCepData = async (postalCode: string) => {
    if (!postalCode) return;
    await fetchAddressData(postalCode).then((res) => {
      forms.setValue('state', res?.uf);
      forms.setValue('city', res?.localidade);
      forms.setValue('province', res?.bairro);
      forms.setValue('address', res?.logradouro);
    });
    await formatAddress(postalCode).then((res) => setFormattedAddress(res));
  };

  const getResponsibleDefaultValues = (): ResponsibleFormValues => {
    if (userToUpdate && mode === 'UPDATE') {
      return {
        displayName: userToUpdate.displayName,
        fullName: userToUpdate.fullName,
        email: userToUpdate.email,
        active: userToUpdate.active,
        role: userToUpdate.role,
        notification: {
          notifyEmail: userToUpdate.notifyEmail,
          notifyWhatsapp: userToUpdate.notifyWhatsapp,
        },
        cpf: formatCpf(userToUpdate.responsible.cpf),
        phone: formatPhone(userToUpdate.responsible.phone),
        postalCode: formatCEP(userToUpdate.responsible.postalCode),
        state: '',
        city: '',
        province: '',
        address: '',
        addressNumber: userToUpdate.responsible.addressNumber,
        addressComplement: userToUpdate.responsible.addressComplement,
        hourElementaryChargeValue: formatMoney(
          userToUpdate.responsible.hourElementaryChargeValue
        ),
        hourUniversityChargeValue: formatMoney(
          userToUpdate.responsible.hourUniversityChargeValue
        ),
        isStudent: userToUpdate.responsible.isStudent,
        birthdate: userToUpdate.responsible.isStudent
          ? dayjs(userToUpdate.student.birthdate)
          : undefined,
        homeClassesPlace: formattedAddress,
      };
    }

    return {
      fullName: '',
      displayName: '',
      email: '',
      cpf: '',
      phone: '',
      postalCode: '',
      state: '',
      city: '',
      province: '',
      address: '',
      addressNumber: '',
      addressComplement: '',
      hourElementaryChargeValue: formatMoney(0),
      hourUniversityChargeValue: formatMoney(0),
      notification: { notifyEmail: true, notifyWhatsapp: true },
      isStudent: false,
      birthdate: undefined,
      homeClassesPlace: '',
    };
  };

  const forms = useForm<ResponsibleFormValues>({
    mode: 'all',
    defaultValues: getResponsibleDefaultValues(),
  });

  const fullName = forms.watch('fullName');

  useEffect(() => {
    if (mode === 'CREATE') {
      forms.setValue('displayName', fullName);
    }
  }, [fullName, mode, forms]);
  const isStudent = forms.watch('isStudent');

  const postalCode = forms.watch('postalCode');

  const eraseAddressData = () => {
    forms.reset({
      postalCode: '',
      province: '',
      city: '',
      state: '',
      address: '',
      addressComplement: '',
      addressNumber: '',
    });
  };

  useEffect(() => {
    handleViaCepData(postalCode);
  }, []);

  const onSubmit = async (data: ResponsibleFormValues) => {
    setIsLoading(true);

    const {
      cpf,
      phone,
      postalCode,
      state,
      city,
      province,
      address,
      hourElementaryChargeValue,
      hourUniversityChargeValue,
      fullName,
      addressComplement,
      notification,
      birthdate,
      homeClassesPlace,
      ...newData
    } = data;

    const updateAPImethod = (data: UpdateUserReq) =>
      userToUpdate && !selfEdit
        ? usersService.updateUserById(userToUpdate.id, data)
        : usersService.updateSelfUser(data);

    if (mode === 'UPDATE') {
      await updateAPImethod({
        displayName: newData.displayName,
        fullName: isAdminOrEmployee ? fullName : undefined,
        email: isAdminOrEmployee ? newData.email : undefined,
        ...notification,
        role: newData.role,
        active: isAdminOrEmployee ? newData.active : undefined,
        [UserRole.RESPONSIBLE]: {
          phone: isAdminOrEmployee ? removeNonNumericChars(phone) : undefined,
          // birthdate: String(birthdate?.toISOString()),
          cpf: undefined,
          postalCode: isAdminOrEmployee
            ? removeNonNumericChars(postalCode)
            : undefined,
          province: isAdminOrEmployee ? data.province : undefined,
          address: isAdminOrEmployee ? data.address : undefined,
          addressNumber: isAdminOrEmployee ? newData.addressNumber : undefined,
          addressComplement: isAdminOrEmployee ? addressComplement : undefined,
          hourElementaryChargeValue: isAdminOrEmployee
            ? readMoney(hourElementaryChargeValue)
            : undefined,
          hourUniversityChargeValue: isAdminOrEmployee
            ? readMoney(hourUniversityChargeValue)
            : undefined,
          homeClassesPlace: formattedAddress,
        },
      })
        .then(() => {
          toast.success('Responsável alterado!');
          navigate(-1);
        })
        .finally(() => {
          setIsLoading(false);
        });
      return;
    }

    await apiService
      .registerResponsibleAndStudent({
        ...newData,
        ...notification,
        cpf: removeNonNumericChars(cpf),
        fullName: fullName,
        birthdate: birthdate?.toDate(),
        phone: removeNonNumericChars(phone),
        postalCode: removeNonNumericChars(postalCode),
        hourElementaryChargeValue: readMoney(hourElementaryChargeValue),
        hourUniversityChargeValue: readMoney(hourUniversityChargeValue),
        addressComplement,
      })
      .then(() => {
        forms.reset(getResponsibleDefaultValues());
        toast.success('Responsável cadastrado!');
      })
      .catch((err) => {
        if (err.response?.data?.errors) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          err.response?.data?.errors.forEach((e: any) => {
            forms.setError(e.property, { message: capitalize(e.errors[0]) });
          });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleChangeMaskCPF = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    forms.setValue('cpf', formatCpf(event.target.value));
  };

  const handleChangeMaskPhone = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    forms.setValue('phone', formatPhone(event.target.value));
  };

  const handleChangeMaskCEP = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    forms.setValue('postalCode', formatCEP(event.target.value));
  };

  const handleChangeMaskMoney = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    fieldName:
      | 'hourElementaryChargeValue'
      | 'hourUniversityChargeValue' = 'hourElementaryChargeValue'
  ) => {
    forms.setValue(fieldName, formatStringMoney(event.target.value));
  };

  useEffect(() => {
    forms[isStudent ? 'register' : 'unregister']('birthdate');
  }, [forms, isStudent]);

  return (
    <FormProvider {...forms}>
      <form
        onSubmit={forms.handleSubmit(onSubmit)}
        className="flex flex-col items-center gap-2 w-full box-border pt-2 p-4 justify-between"
      >
        <Input
          name="fullName"
          label="Nome Completo"
          rules={formRules.fullNameRequired}
          disabled={selfEdit}
          autoFocus
        />

        <Input name="displayName" label="Como gostaria de ser chamado(a)?" />

        <Input
          name="email"
          type="email"
          label="E-mail"
          rules={formRules.emailRequired}
          disabled={selfEdit}
        />

        <Input
          name="cpf"
          label="CPF"
          rules={formRules.cpfRequired}
          onChange={handleChangeMaskCPF}
          disabled={selfEdit}
        />

        <Input
          name="phone"
          label="Telefone"
          rules={formRules.phoneRequired}
          onChange={handleChangeMaskPhone}
          disabled={selfEdit}
        />

        <Input
          name="postalCode"
          label="CEP"
          rules={formRules.postalCodeRequired}
          onChange={(e) => {
            handleChangeMaskCEP(e);
            handleViaCepData(e.target.value);
          }}
          disabled={selfEdit}
        />

        <div className="flex flex-row flex-wrap gap-2 w-full">
          <div className="flex-grow sm:w-auto">
            <Input name="address" label="Endereço" disabled />
          </div>
          <div className="w-full sm:w-1/5">
            <Input
              name="addressNumber"
              label="Número"
              placeholder="lote/casa/apt"
              rules={formRules.addressNumberRequired}
              disabled={selfEdit}
            />
          </div>
        </div>

        <Input
          name="addressComplement"
          label="Complemento"
          disabled={selfEdit}
        />

        <div className="flex flex-row flex-wrap gap-2 w-full">
          <div className="w-full md:w-2/5">
            <Input name="province" label="Bairro" disabled />
          </div>

          <div className="w-full sm:w-4/5 md:w-2/5">
            <Input name="city" label="Cidade" disabled />
          </div>

          <div className="flex-grow w-full sm:w-24 ">
            <Input name="state" label="Estado" disabled />
          </div>
        </div>

        <Switch
          label="Estudante"
          name="isStudent"
          disabled={mode === 'UPDATE'}
        />

        {isStudent && (
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="pt-br"
          >
            <Datepicker
              disabled={mode === 'UPDATE' || !isStudent}
              className="w-full"
              labelOut
              name="birthdate"
              label="Data de Nascimento"
              rules={isStudent ? formRules.birthdateRequired : undefined}
            />
          </LocalizationProvider>
        )}

        <Input
          name="hourElementaryChargeValue"
          label="Valor da Hora/Aula do Ensino Fundamental / Médio / Técnico"
          rules={formRules.hourPriceRequired}
          onChange={handleChangeMaskMoney}
          disabled={selfEdit}
        />

        <Input
          name="hourUniversityChargeValue"
          label="Valor da Hora/Aula do Ensino Superior"
          rules={formRules.hourPriceRequired}
          onChange={(event) =>
            handleChangeMaskMoney(event, 'hourUniversityChargeValue')
          }
          disabled={selfEdit}
        />

        <Notification
          label="Notificações"
          name="notification"
          disableEmailField={mode === 'CREATE'}
        />

        <Button type="submit" isLoading={isLoading} circular>
          {mode === 'CREATE' ? 'Cadastrar' : 'Alterar'}
        </Button>
      </form>
    </FormProvider>
  );
}
